import { MetricCard } from './metric-card';
import { metricDefs } from '../metric-defs';
import { SCALAR_METRIC_KEYS } from '@/types/analyticsV2';
import type { ScalarMetricResult } from '@/types/analyticsV2';
import type { MetricDef } from '../metric-defs';
import { useMetricsPerKey } from '@/hooks/use-analytics-v2';

interface OverviewSectionProps {
  fromDate: string;
  toDate: string;
  /**
   * Human-readable label of the previous period the trend is computed
   * against. Passed to each card so the trend pill tooltip can show
   * "vs Mar 2 – Mar 31".
   */
  comparisonLabel?: string;
  /** Super-admin only: render metrics for this account instead of the caller's own. */
  overrideAccountId?: number;
}

const cardValue = (
  def: MetricDef,
  result: ScalarMetricResult | undefined,
): { value: number | null; denominator?: number; unit?: string } => {
  if (!result) return { value: null };
  switch (def.cardDisplay) {
    case 'count-with-denominator':
      return { value: result.current.count, denominator: result.current.total };
    case 'percent':
      return { value: result.current.percent, unit: '%' };
  }
};

const cardTrend = (def: MetricDef, result: ScalarMetricResult | undefined): number | null => {
  if (!result) return null;
  return result.trend[def.trendField];
};

export function OverviewSection({ fromDate, toDate, comparisonLabel, overrideAccountId }: OverviewSectionProps) {
  // Fan out one request per metric key so each card renders the moment its
  // own query resolves, instead of waiting for the slowest of the six.
  const results = useMetricsPerKey(SCALAR_METRIC_KEYS, fromDate, toDate, overrideAccountId);
  const byKey = Object.fromEntries(results.map((r) => [r.key, r]));

  return (
    <div className='flex flex-col gap-3'>
      <h2 className='text-[12px] text-gray-800 font-medium'>Overview</h2>
      <div className='overflow-x-auto'>
        <div className='grid grid-cols-3 grid-rows-2 gap-3 auto-cols-[minmax(260px,1fr)] min-w-[840px]'>
          {SCALAR_METRIC_KEYS.map((key) => {
            const def = metricDefs[key];
            const r = byKey[key];
            const { value, denominator, unit } = cardValue(def, r?.data);
            return (
              <MetricCard
                key={key}
                title={def.label}
                description={def.description}
                value={value}
                denominator={denominator}
                unit={unit}
                trend={cardTrend(def, r?.data)}
                comparisonLabel={comparisonLabel}
                isLoading={r?.isLoading ?? false}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
